export const GROUP_CATEGORY_NZENGO = "NZENGO";
export const GROUP_CATEGORY_NORMAL = "NORMAL";
export const GROUP_CATEGORY_STORAGE_KEY = "savvy:group-category-prototype";
export const ACTIVE_GROUP_CATEGORY_KEY = "savvy:active-group-category";
const ACTIVE_GROUP_ID_KEY = "savvy:active-group-id";
const ACTIVE_GROUP_NAME_KEY = "savvy:active-group-name";

const normalize = (value) => String(value || "").trim().toUpperCase();

const resolveCategory = (value) => {
  const normalized = normalize(value);
  if (!normalized) return null;
  if (normalized === GROUP_CATEGORY_NZENGO || normalized.includes("NZENGO")) {
    return GROUP_CATEGORY_NZENGO;
  }
  return GROUP_CATEGORY_NORMAL;
};

const readLocalStorage = (key) => {
  try {
    return localStorage.getItem(key);
  } catch (_error) {
    return null;
  }
};

const writeLocalStorage = (key, value) => {
  try {
    localStorage.setItem(key, value);
  } catch (_error) {
    // Ignore storage failures in restricted browser contexts.
  }
};

export const readStoredGroupCategories = () => {
  try {
    return JSON.parse(readLocalStorage(GROUP_CATEGORY_STORAGE_KEY) || "{}");
  } catch (_error) {
    return {};
  }
};

const getGroupCategoryKeys = (group = {}) => [
  group.id,
  group.group_id,
  group.groupId,
  group.group?.id,
  group.active_group?.id,
  group.name,
  group.group_name,
  group.groupName,
  group.group?.name,
  group.active_group?.name,
]
  .filter((value) => value !== undefined && value !== null && String(value).trim() !== "")
  .map((value) => String(value).trim());

export const getStoredGroupCategory = (group = {}) => {
  const stored = readStoredGroupCategories();
  const key = getGroupCategoryKeys(group).find((candidate) => stored[candidate]);
  return key ? resolveCategory(stored[key]) : null;
};

export const writeStoredGroupCategory = (group, category) => {
  const resolved = resolveCategory(category) || GROUP_CATEGORY_NORMAL;
  const keys = Array.from(new Set(getGroupCategoryKeys(group)));
  if (!keys.length) return;
  const stored = readStoredGroupCategories();
  keys.forEach((key) => {
    stored[key] = resolved;
  });
  writeLocalStorage(GROUP_CATEGORY_STORAGE_KEY, JSON.stringify(stored));
};

export const writeActiveGroupContext = (group = {}) => {
  const category = getGroupCategory(group);
  writeLocalStorage(ACTIVE_GROUP_CATEGORY_KEY, category);
  const id = group.id || group.group_id || group.groupId || group.group?.id || group.active_group?.id;
  const name = group.name || group.group_name || group.groupName || group.group?.name || group.active_group?.name;
  if (id) writeLocalStorage(ACTIVE_GROUP_ID_KEY, String(id));
  if (name) writeLocalStorage(ACTIVE_GROUP_NAME_KEY, String(name));
};

const getStoredActiveGroupCategory = (group = {}) => {
  const storedCategory = resolveCategory(readLocalStorage(ACTIVE_GROUP_CATEGORY_KEY));
  if (!storedCategory) return null;
  const storedId = readLocalStorage(ACTIVE_GROUP_ID_KEY);
  const storedName = readLocalStorage(ACTIVE_GROUP_NAME_KEY);
  const keys = getGroupCategoryKeys(group);
  if (storedId && keys.includes(String(storedId))) return storedCategory;
  if (storedName && keys.includes(String(storedName))) return storedCategory;
  return null;
};

export const getGroupCategory = (group = {}) => {
  const stored = getStoredGroupCategory(group);
  if (stored) return stored;

  const activeStored = getStoredActiveGroupCategory(group);
  if (activeStored) return activeStored;

  const explicit =
    group.group_category ||
    group.groupCategory ||
    group.category ||
    group.group?.group_category ||
    group.active_group?.group_category;

  const explicitCategory = resolveCategory(explicit);
  if (explicitCategory) return explicitCategory;

  const name = [
    group.name,
    group.group_name,
    group.groupName,
    group.group?.name,
    group.active_group?.name,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

  if (name.includes("nzengo")) {
    return GROUP_CATEGORY_NZENGO;
  }

  return GROUP_CATEGORY_NORMAL;
};

export const isNzengoGroup = (group = {}) => getGroupCategory(group) === GROUP_CATEGORY_NZENGO;

export const supportsLoans = (group = {}) => !isNzengoGroup(group);

export const supportsUpatu = (group = {}) => !isNzengoGroup(group);

export const withResolvedGroupCategory = (group = {}) => ({
  ...group,
  group_category: getGroupCategory(group),
});

export const getActiveGroupFromUser = (user = {}) => {
  const candidate = {
    id: user?.group_id || user?.group?.id || user?.active_group?.id,
    group_id: user?.group_id || user?.group?.id || user?.active_group?.id,
    name: user?.group_name || user?.group?.name || user?.active_group?.name,
    group_name: user?.group_name || user?.group?.name || user?.active_group?.name,
    group_category: user?.group_category || user?.group?.group_category || user?.active_group?.group_category,
    group: user?.group,
    active_group: user?.active_group,
    status: user?.member_status || user?.membership_status || "ACTIVE",
  };
  return withResolvedGroupCategory(candidate);
};
